import _ from 'lodash'
import axios, { AxiosRequestConfig } from 'axios'
import { CommonState, Model } from '../classes/Model'
import { Consultation } from './consultation.model'
import { Patient } from './patient.model'

// Define as const to use as a type AND value
export const objectTypes = {
  CONSULTATION: 'consultation' as const,
  ESTABLISHMENT: 'establishment' as const,
  PATIENT: 'patient' as const,
  EXPERTISE: 'expertise' as const,
  AGENT_CHAT_MESSAGE: 'agent_chat_message' as const,
  AGENT_CHAT_SESSION: 'agent_chat_session' as const,
  KNOWLEDGE_BASE: 'knowledge_base' as const,
  FORM_TEMPLATE: 'form_template' as const,
} as const

// Keep the original type definition for backward compatibility
export type ObjectType = typeof objectTypes[keyof typeof objectTypes]

export type RecordingAnnotation = {
  id: string
  start: number
  end: number
  note: string
}

export type CreatePresignedUrlRequest = {
  fileName: string
  fileType: string
  objectType: ObjectType
  objectId: string
  sessionId?: string
}

export type Document = {
  filename: string
  isRecording?: boolean
  isBillingJustification?: boolean
  key: string
  objectType: ObjectType
  recordingAnnotations?: RecordingAnnotation[]
  url: string
  uploadedBy?: string
}

export type FileUrlResponse = {
  key: string
  filename: string
  url: string
}

export type GetFileUrlByKeyArg = {
  key: string
}

export type FindFilesByObjectArg = {
  objectId: string
  objectType: ObjectType
}

export type DictaphoneDocument = Document & {
  text?: string
}

export type CreateDocumentDto = Omit<Document, 'url' | 'recordingAnnotations' | 'uploadedBy'>

export type UpdateDocumentDto = Partial<CreateDocumentDto>

export type GetFileByKeyRequest = {
  key: string
}

export type GetFileByObjectRequest = {
  objectType: ObjectType
  objectId: string
  filename: string
}

export type GetFilesByCriteriaRequest = {
  objectType: ObjectType
  objectId: string
  filenamePattern?: string
}

export type DocumentState = CommonState & {
  documents: {
    [documentsByObjectType: string]: {
      [objectId: string]: {
        [key: string]: Document
      }
    }
  }
  qrcode: string
}

class DocumentModel extends Model {
  static instance: DocumentModel

  static getInstance() {
    if (!DocumentModel.instance) {
      DocumentModel.instance = new DocumentModel('document')
    }
    return DocumentModel.instance
  }

  getInitialState(): DocumentState {
    return _.merge({}, this.commonState, {
      requestTracker: {
        qrcode: {
          loading: false,
          error: null,
        },
      },
      documents: {
        // documentsByConsultationId: {},
        // documentsByPatientId: {}
      },
      qrcode: '',
    })
  }

  generatePresignedUrl = async ({ fileName, fileType, objectType, objectId, sessionId }: CreatePresignedUrlRequest): Promise<{ url: string; fileName: string; key: string }> => {
    return await this.httpClient.request('/files/generate-presigned-url', 'POST', { fileName, fileType, objectType, objectId, sessionId })
  }

  uploadDocumentToAwsS3 = async (url: string, document: File) => {
    const headers = {
      'Content-Type': document.type,
    }
    const requestConfig: AxiosRequestConfig = {
      url,
      method: 'PUT',
      headers,
      data: document,
    }
    const response = await axios(requestConfig)
    return response.data
  }

  addDocument = async (documentDto: CreateDocumentDto, objectType: ObjectType, objectId: string) => {
    return await this.httpClient.request<Document>(`/files/${objectType}/${objectId}`, 'POST', { file: documentDto })
  }

  addDictaphoneDocument = async (documentDto: CreateDocumentDto, objectId: string) => {
    return await this.httpClient.request<Document>(`/files/consultations/${objectId}/dictaphone`, 'POST', { file: documentDto })
  }

  addBiologicalDocument = async (documentDto: CreateDocumentDto, objectType: ObjectType, objectId: string, biologicalId: string) => {
    return await this.httpClient.request<Consultation>(`/files/${objectType}/${objectId}/biologicals/${biologicalId}/`, 'POST', { file: documentDto })
  }

  deleteDocuments = async (documents: Omit<Document, 'url'>[], objectType: ObjectType, objectId: string) => {
    await this.httpClient.request(`/files/${objectType}/${objectId}/delete`, 'POST', { files: documents })
    return
  }

  generateConsultationReport = async (consultationId: string, reportValues: Record<string, any>) => {
    return await this.httpClient.request(`/files/consultation/${consultationId}/report`, 'POST', reportValues)
  }

  sendDocumentsByEmail = async (consultationId: string, emails: string[], filenames: string[], pdfPassword?: string) => {
    return await this.httpClient.request(`/files/consultation/${consultationId}/send-emails`, 'POST', {
      emails,
      filenames,
      ...(pdfPassword ? { pdfPassword } : {}),
    })
  }

  runUploadSpeedTest = async (data: string) => {
    await this.httpClient.request('/files/upload/speed/test', 'POST', { data })
  }

  generateQrCode = async (url: string): Promise<{ url: string }> => {
    return await this.httpClient.request<{ url: string }>(`/short/shorten`, 'POST', { url: url })
  }

  updateFilename = async (file: Omit<Document, 'url'>, newFilename: string, objectType: ObjectType, objectId: string): Promise<Consultation | Patient> => {
    return await this.httpClient.request<Consultation | Patient>(`/files/${objectType}/${objectId}/update-name`, 'PATCH', {
      file,
      update: { filename: newFilename },
    })
  }

  updateRecordingAnnotation = async (fileKey: string, annotation: RecordingAnnotation, objectType: ObjectType, objectId: string) => {
    return await this.httpClient.request(`/files/${objectType}/${objectId}/recording-annotation`, 'PATCH', { fileKey, annotation })
  }

  deleteRecordingAnnotation = async (fileKey: string, annotationId: string, objectType: ObjectType, objectId: string) => {
    return await this.httpClient.request(`/files/${objectType}/${objectId}/recording-annotation`, 'DELETE', { fileKey, annotationId })
  }

  addPrescription = async (consultationId: string, prescriptionId: string) => {
    return await this.httpClient.request(`/files/consultation/${consultationId}/prescription`, 'POST', { prescriptionId })
  }

  // New methods for retrieving files with signed URLs
  getFileByKey = async (key: string): Promise<Document> => {
    return await this.httpClient.request<Document>('/files/get-file-by-key', 'POST', { key })
  }

  getFileByObject = async (objectType: ObjectType, objectId: string, filename: string): Promise<Document> => {
    return await this.httpClient.request<Document>('/files/get-file-by-object', 'POST', {
      objectType,
      objectId,
      filename,
    })
  }

  findFilesByCriteria = async (objectType: ObjectType, objectId: string, filenamePattern?: string): Promise<Document[]> => {
    return await this.httpClient.request<Document[]>('/files/find-files', 'POST', {
      objectType,
      objectId,
      filenamePattern,
    })
  }
}

export default DocumentModel.getInstance()
